import type { PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FileQuestion } from "lucide-react";

import { cn } from "../../utils";
import { FileIcon } from "../FileIcon";
import { formatSkillPathForDisplay } from "../utils";

import {
  isSkillMarkdownRelativeLink,
  resolveSkillMarkdownRelativePath,
} from "./utils";
import { SkillMarkdownPreviewContent } from "./SkillMarkdownPreviewContent";

const HIDE_DELAY_MS = 80;
const MAX_CODE_PREVIEW_LINES = 24;
const PREVIEW_GAP_PX = 10;
const PREVIEW_TRANSITION_MS = 150;
const VIEWPORT_PADDING_PX = 12;

interface ActivePreview {
  anchor: HTMLAnchorElement;
  fileExists: boolean;
  targetPath: string;
}

export interface SkillMarkdownLinkPreviewLayerProps {
  children: ReactNode;
  filePath?: string | null;
  hasLinkedFile?: (path: string) => boolean;
  getLinkedFileContent?: (path: string) => string | undefined;
}

const trimCodePreview = (content: string): string => {
  const lines = content.split("\n");
  if (lines.length <= MAX_CODE_PREVIEW_LINES) {
    return content;
  }

  return `${lines.slice(0, MAX_CODE_PREVIEW_LINES).join("\n")}\n…`;
};

export const SkillMarkdownLinkPreviewLayer = ({
  children,
  filePath,
  hasLinkedFile,
  getLinkedFileContent,
}: SkillMarkdownLinkPreviewLayerProps) => {
  const [activePreview, setActivePreview] = useState<ActivePreview | null>(
    null,
  );
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const [isPositioned, setIsPositioned] = useState(false);
  const hideTimerRef = useRef<number | null>(null);
  const unmountTimerRef = useRef<number | null>(null);
  const showFrameRef = useRef<number | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const clearUnmountTimer = useCallback(() => {
    if (unmountTimerRef.current !== null) {
      window.clearTimeout(unmountTimerRef.current);
      unmountTimerRef.current = null;
    }
  }, []);

  const clearShowFrame = useCallback(() => {
    if (showFrameRef.current !== null) {
      window.cancelAnimationFrame(showFrameRef.current);
      showFrameRef.current = null;
    }
  }, []);

  const hidePreview = useCallback(() => {
    clearHideTimer();
    clearShowFrame();
    setIsVisible(false);
    clearUnmountTimer();
    unmountTimerRef.current = window.setTimeout(() => {
      unmountTimerRef.current = null;
      setActivePreview(null);
      setIsPositioned(false);
    }, PREVIEW_TRANSITION_MS);
  }, [clearHideTimer, clearShowFrame, clearUnmountTimer]);

  const scheduleHide = useCallback(() => {
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => {
      hideTimerRef.current = null;
      hidePreview();
    }, HIDE_DELAY_MS);
  }, [clearHideTimer, hidePreview]);

  const resolvePreview = useCallback(
    (anchor: HTMLAnchorElement): ActivePreview | null => {
      if (!hasLinkedFile && !getLinkedFileContent) {
        return null;
      }

      const href = anchor.getAttribute("href");
      if (!href || !isSkillMarkdownRelativeLink(href)) {
        return null;
      }

      const targetPath = resolveSkillMarkdownRelativePath(
        filePath ?? null,
        href,
      );
      if (!targetPath) {
        return null;
      }

      const fileExists =
        hasLinkedFile?.(targetPath) ??
        getLinkedFileContent?.(targetPath) !== undefined;

      return { anchor, fileExists, targetPath };
    },
    [filePath, getLinkedFileContent, hasLinkedFile],
  );

  const showPreview = useCallback(
    (anchor: HTMLAnchorElement) => {
      const nextPreview = resolvePreview(anchor);
      if (!nextPreview) {
        return;
      }

      clearHideTimer();
      clearUnmountTimer();
      const isSamePreview =
        activePreview &&
        activePreview.anchor === nextPreview.anchor &&
        activePreview.targetPath === nextPreview.targetPath;

      if (isSamePreview) {
        setIsVisible(true);
        return;
      }

      setIsVisible(false);
      setActivePreview(nextPreview);
    },
    [activePreview, clearHideTimer, clearUnmountTimer, resolvePreview],
  );

  const handlePointerOver = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const { target } = event;
      if (!(target instanceof Element)) {
        return;
      }

      const anchor = target.closest("a");
      if (anchor instanceof HTMLAnchorElement) {
        showPreview(anchor);
      }
    },
    [showPreview],
  );

  const handlePointerOut = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const { relatedTarget, target } = event;
      if (!(target instanceof Element)) {
        return;
      }

      const anchor = target.closest("a");
      if (!(anchor instanceof HTMLAnchorElement)) {
        return;
      }

      if (
        relatedTarget instanceof Node &&
        (anchor.contains(relatedTarget) ||
          previewRef.current?.contains(relatedTarget))
      ) {
        return;
      }

      scheduleHide();
    },
    [scheduleHide],
  );

  const previewContent =
    activePreview?.fileExists && activePreview
      ? getLinkedFileContent?.(activePreview.targetPath)
      : undefined;
  const isMarkdownTarget =
    activePreview?.targetPath.toLowerCase().endsWith(".md") ?? false;

  const updatePosition = useCallback(() => {
    if (!activePreview?.anchor || !previewRef.current) {
      return;
    }

    const anchorRect = activePreview.anchor.getBoundingClientRect();
    if (anchorRect.width === 0 && anchorRect.height === 0) {
      hidePreview();
      return;
    }

    const previewRect = previewRef.current.getBoundingClientRect();
    const { bottom, left: anchorLeft, top: anchorTop } = anchorRect;
    let left = anchorLeft;
    let top = bottom + PREVIEW_GAP_PX;

    if (left + previewRect.width > window.innerWidth - VIEWPORT_PADDING_PX) {
      left = window.innerWidth - previewRect.width - VIEWPORT_PADDING_PX;
    }

    left = Math.max(VIEWPORT_PADDING_PX, left);

    if (top + previewRect.height > window.innerHeight - VIEWPORT_PADDING_PX) {
      const anchoredAbove = anchorTop - previewRect.height - PREVIEW_GAP_PX;
      if (anchoredAbove >= VIEWPORT_PADDING_PX) {
        top = anchoredAbove;
      } else {
        top = Math.max(
          VIEWPORT_PADDING_PX,
          window.innerHeight - previewRect.height - VIEWPORT_PADDING_PX,
        );
      }
    }

    setPosition({ left, top });
    setIsPositioned(true);
  }, [activePreview, hidePreview]);

  useEffect(
    () => () => {
      clearHideTimer();
      clearUnmountTimer();
      clearShowFrame();
    },
    [clearHideTimer, clearShowFrame, clearUnmountTimer],
  );

  useEffect(() => {
    if (!activePreview) {
      return;
    }

    setIsPositioned(false);
    const animationFrame = window.requestAnimationFrame(updatePosition);
    const handleWindowUpdate = () => updatePosition();
    let resizeObserver: ResizeObserver | null = null;

    if (typeof ResizeObserver !== "undefined" && previewRef.current) {
      resizeObserver = new ResizeObserver(handleWindowUpdate);
      resizeObserver.observe(previewRef.current);
    }

    window.addEventListener("resize", handleWindowUpdate);
    window.addEventListener("scroll", handleWindowUpdate, true);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", handleWindowUpdate);
      window.removeEventListener("scroll", handleWindowUpdate, true);
    };
  }, [activePreview, updatePosition]);

  useEffect(() => {
    if (!activePreview || !isPositioned) {
      return;
    }

    clearShowFrame();
    showFrameRef.current = window.requestAnimationFrame(() => {
      showFrameRef.current = null;
      setIsVisible(true);
    });

    return clearShowFrame;
  }, [activePreview, clearShowFrame, isPositioned]);

  let previewBody: ReactNode;
  if (activePreview && !activePreview.fileExists) {
    previewBody = (
      <div className="flex min-h-[220px] items-center justify-center p-8">
        <div className="max-w-sm px-6 py-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400">
            <FileQuestion className="h-6 w-6" />
          </div>
          <p className="text-base font-medium text-gray-900">
            No file has been found
          </p>
          <p className="mt-2 text-sm leading-6 text-gray-500">
            We couldn&apos;t find this linked file in the current skill
            collection.
          </p>
        </div>
      </div>
    );
  } else if (previewContent === undefined) {
    previewBody = (
      <p className="text-sm text-gray-500">
        Preview unavailable for this file.
      </p>
    );
  } else if (isMarkdownTarget) {
    previewBody = (
      <SkillMarkdownPreviewContent
        markdown={previewContent}
        containerClassName="flex flex-col overflow-visible min-w-[360px]"
        bodyContainerClassName="flex flex-col overflow-visible"
        editorContentClassName="skill-markdown-preview-expand-width"
      />
    );
  } else {
    previewBody = (
      <pre className="overflow-hidden whitespace-pre-wrap break-words font-mono text-[11px] leading-5 text-gray-700">
        {trimCodePreview(previewContent)}
      </pre>
    );
  }

  return (
    <>
      <div onPointerOut={handlePointerOut} onPointerOver={handlePointerOver}>
        {children}
      </div>
      {activePreview && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={previewRef}
              className={cn(
                "fixed z-[120] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-gray-950/10",
                "transform-gpu transition-[opacity,transform] ease-out",
                isVisible
                  ? "translate-y-0 scale-100 opacity-100 duration-150"
                  : "pointer-events-none translate-y-1 scale-[0.98] opacity-0 duration-150",
              )}
              style={{
                left: position.left,
                top: position.top,
                visibility: isPositioned ? "visible" : "hidden",
                maxWidth: "min(820px, calc(100vw - 24px))",
                minWidth: "min(360px, calc(100vw - 24px))",
                width: "fit-content",
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onPointerEnter={clearHideTimer}
              onPointerLeave={scheduleHide}
            >
              <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50/80 px-3 py-2">
                <FileIcon path={activePreview.targetPath} />
                <span className="truncate text-xs font-medium text-gray-700">
                  {formatSkillPathForDisplay(activePreview.targetPath, { separator: " > " })}
                </span>
              </div>
              <div
                className={cn(
                  "max-h-[320px] overflow-auto",
                  !activePreview.fileExists || isMarkdownTarget
                    ? ""
                    : "px-3 py-3",
                )}
              >
                {previewBody}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
};
